/**
 * LotowheelModalAlgoritmo — extensión del modal: tab Algoritmo.
 *
 * Extiende `LotowheelModule` (lotowheel.js) con métodos para configurar
 * el resolver matemático del sorteo. Object.assign sobre el módulo, no
 * declara nuevo namespace.
 *
 * Mapa de secciones:
 *   1. RESOLVER       loader + select de algoritmo + presets de marketing.
 *   2. PERFIL OPERADOR  4 sliders que mapean a parámetros del resolver
 *                       (volatilidad, recorte, agresividad, etc).
 *
 * Componentes externos:
 *   - LotowheelSimulator (modal de simulación, accesible desde aquí).
 *   - MOSTRAR_PARAMS_AVANZADOS (flag de visibilidad de inputs avanzados).
 */

import { LotowheelModule, MOSTRAR_PARAMS_AVANZADOS } from './lotowheel.js';
import { App, Api } from '../../../admin.js';
import { LotowheelSimulator } from './lotowheel-simulator.js';
import { ROL_ADMIN } from '@shared/config/business-constants.js';
import { t } from '../../../services/i18n-admin.js';
import {
    CLAVES_DE_RTP_POR_CANAL,
    componerConfigDePresetConRtpPorCanal,
    leerRtpPorCanalExigiendoTodos,
} from '@shared/config/games/lotowheel/preset-de-resolver.js';

Object.assign(LotowheelModule, {
    // ═══════════════════════════════════════════════════════════════════════
    // RESOLVER — algoritmo de resolución del sorteo + presets de marketing.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Carga resolvers disponibles y presets desde el backend.
     * Popula los dropdowns del panel Resolución.
     */
    async loadResolverOptions() {
        try {
            const [resolversRes, presetsRes, profilesRes] = await Promise.all([
                Api.callAdmin('/admin/resolvers/lotowheel', 'GET'),
                Api.callAdmin('/admin/resolver-presets/lotowheel', 'GET'),
                Api.callAdmin('/admin/resolver-profiles/slider-definitions', 'GET'),
            ]);

            this._resolverCache = resolversRes.resolvers || [];
            this._resolverPresetsCache = presetsRes.presets || [];

            // Perfil de operador (sliders)
            this._sliderDefinitions = profilesRes.sliders || [];
            this._renderProfileSliders();

            // Popular dropdown de resolvers
            const select = document.getElementById('resolverName');
            select.innerHTML = '';
            for (let i = 0; i < this._resolverCache.length; i++) {
                const r = this._resolverCache[i];
                const opt = document.createElement('option');
                opt.value = r.name;
                opt.textContent = r.displayName;
                select.appendChild(opt);
            }

            // Popular dropdown de presets
            this._renderPresetCards();

            // Event listeners
            select.onchange = () => this.onResolverChange();
        } catch (err) {
            console.error('[LotowheelModule] Error cargando resolvers:', err);
        }
    },

    /**
     * Filtra y guarda en cache los presets del resolver activo.
     * El dropdown fuzzy los consume desde _presetsParaResolver.
     */
    _renderPresetCards() {
        if (!this._resolverPresetsCache) return;
        const currentResolver = document.getElementById('resolverName')?.value;
        this._presetsParaResolver = [];
        for (let i = 0; i < this._resolverPresetsCache.length; i++) {
            if (this._resolverPresetsCache[i].resolver_name === currentResolver) {
                this._presetsParaResolver.push(this._resolverPresetsCache[i]);
            }
        }
        // Limpiar búsqueda, pero preservar el nombre activo si hay uno
        // (_clearPresetSearch se llama también desde loadPreset vía onResolverChange,
        // y borraría el nombre que _selectPresetFromDropdown acababa de escribir)
        const nombreActivo = this._activePresetNombre;
        this._clearPresetSearch();
        if (nombreActivo) {
            const input = document.getElementById('presetSearchInput');
            const clearBtn = document.getElementById('presetSearchClear');
            if (input) input.value = nombreActivo;
            if (clearBtn) clearBtn.style.display = '';
        }
    },

    /** Muestra el dropdown con todos los presets al enfocar el input */
    _onPresetSearchFocus() {
        this._showPresetDropdown(document.getElementById('presetSearchInput')?.value || '');
    },

    /** Filtra el dropdown al escribir */
    _onPresetSearch(query) {
        const clearBtn = document.getElementById('presetSearchClear');
        if (clearBtn) clearBtn.style.display = query ? '' : 'none';
        this._showPresetDropdown(query);
    },

    /** Muestra el dropdown filtrando por query (vacío = todos) */
    _showPresetDropdown(query) {
        const dropdown = document.getElementById('presetDropdown');
        if (!dropdown) return;
        const presets = this._presetsParaResolver || [];

        // Filtrado fuzzy simple: incluye si cada char del query aparece en orden
        const q = query.toLowerCase();
        const filtered = q
            ? presets.filter(p => {
                  const text = (p.nombre + ' ' + (p.descripcion || '')).toLowerCase();
                  let idx = 0;
                  for (let i = 0; i < q.length; i++) {
                      const found = text.indexOf(q[i], idx);
                      if (found === -1) return false;
                      idx = found + 1;
                  }
                  return true;
              })
            : presets;

        if (filtered.length === 0) {
            dropdown.innerHTML =
                '<div style="padding:10px 12px; font-size:12px; color:var(--text-muted);">' +
                t('Sin resultados') +
                '</div>';
        } else {
            let html = '';
            for (let i = 0; i < filtered.length; i++) {
                const p = filtered[i];
                const desc = p.descripcion
                    ? `<div style="font-size:11px;color:var(--text-muted);margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.descripcion}</div>`
                    : '';
                // Badge de propietario: sistema / operador (naranja) / superadmin (gris)
                let badge = '';
                let avisoOperador = '';
                if (p.es_sistema) {
                    badge =
                        '<span style="font-size:10px;padding:1px 5px;border-radius:3px;background:var(--bg-glass);color:var(--text-muted);margin-left:4px;flex-shrink:0;">' +
                        t('sistema') +
                        '</span>';
                } else if (p.nodo_id) {
                    // Preset creado por un operador — aviso prominente
                    const propietario = p.nodo_nombre || 'Operador';
                    badge = `<span style="font-size:10px;padding:1px 5px;border-radius:3px;background:#ff980020;color:#ff9800;border:1px solid #ff980040;margin-left:4px;flex-shrink:0;" title="Preset de ${propietario}">&#9888; ${propietario}</span>`;
                    if (App.currentUser?.rol === ROL_ADMIN.SUPERADMIN) {
                        const autor = p.creado_por_nombre || 'desconocido';
                        avisoOperador = `<div style="font-size:10px;color:#ff9800;margin-top:2px;">Creado por ${autor} &mdash; cambios afectan a terceros</div>`;
                    }
                }
                html +=
                    `<div data-preset-id="${p.id}" class="preset-dropdown-row"` +
                    ` style="display:flex;flex-direction:column;padding:8px 12px;cursor:pointer;border-bottom:1px solid var(--border-color);transition:background 0.1s;"` +
                    ` onmouseenter="this.style.background='var(--bg-hover)'" onmouseleave="this.style.background=''"` +
                    ` data-action="lotowheel:selectPresetFromDropdown" data-id="${p.id}">` +
                    `<div style="display:flex;align-items:center;gap:4px;"><span style="flex:1;font-size:13px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.nombre}</span>${badge}` +
                    `<span style="display:flex;gap:2px;margin-left:4px;flex-shrink:0;">` +
                    `<button title="${t('Duplicar')}" style="background:none;border:none;cursor:pointer;padding:1px 3px;font-size:12px;color:var(--text-secondary);" data-action="lotowheel:duplicatePreset" data-id="${p.id}">⧉</button>` +
                    `<button title="${t('Renombrar')}" style="background:none;border:none;cursor:pointer;padding:1px 3px;font-size:12px;color:var(--text-secondary);" data-action="lotowheel:renamePreset" data-id="${p.id}">✎</button>` +
                    `<button title="${t('Eliminar')}" style="background:none;border:none;cursor:pointer;padding:1px 3px;font-size:12px;color:#ef5350;" data-action="lotowheel:deletePreset" data-id="${p.id}">✕</button>` +
                    `</span></div>` +
                    desc +
                    avisoOperador +
                    `</div>`;
            }
            dropdown.innerHTML = html;
        }
        dropdown.style.display = '';

        // Cerrar dropdown al hacer click fuera
        if (!this._presetDropdownCloseHandler) {
            this._presetDropdownCloseHandler = e => {
                const wrapper = document.getElementById('presetSearchWrapper');
                const drop = document.getElementById('presetDropdown');
                if (wrapper && !wrapper.contains(e.target) && drop && !drop.contains(e.target)) {
                    if (drop) drop.style.display = 'none';
                }
            };
            document.addEventListener('mousedown', this._presetDropdownCloseHandler);
        }
    },

    /** Selecciona un preset del dropdown: lo carga y actualiza el label */
    _selectPresetFromDropdown(presetId) {
        const preset = this._presetsParaResolver?.find(p => p.id === Number(presetId));
        if (!preset) return;
        // Cerrar dropdown y poner nombre en el input
        const input = document.getElementById('presetSearchInput');
        const dropdown = document.getElementById('presetDropdown');
        const clearBtn = document.getElementById('presetSearchClear');
        const label = document.getElementById('presetActiveLabel');
        if (input) input.value = preset.nombre;
        if (dropdown) dropdown.style.display = 'none';
        if (clearBtn) clearBtn.style.display = '';
        if (label) label.textContent = preset.descripcion || '';
        this.loadPreset(presetId);
    },

    /** Limpia la búsqueda y cierra el dropdown */
    _clearPresetSearch() {
        const input = document.getElementById('presetSearchInput');
        const dropdown = document.getElementById('presetDropdown');
        const clearBtn = document.getElementById('presetSearchClear');
        const label = document.getElementById('presetActiveLabel');
        if (input) input.value = '';
        if (dropdown) dropdown.style.display = 'none';
        if (clearBtn) clearBtn.style.display = 'none';
        if (label) label.textContent = '';
    },

    /**
     * Cuando cambia el resolver seleccionado: renderiza los campos de config dinámicos.
     */
    onResolverChange() {
        const resolverName = document.getElementById('resolverName').value;
        const resolver = this._resolverCache?.find(r => r.name === resolverName);
        if (!resolver) return;

        // Mostrar/ocultar sección de perfil (solo para Lotería Ponderada)
        const profileSection = document.getElementById('profileSection');
        const details = document.getElementById('resolverConfigDetails');
        const isVolatilidadControlada = resolverName === 'volatilidad-controlada';

        if (profileSection) profileSection.style.display = isVolatilidadControlada ? '' : 'none';

        // Parámetros Avanzados: solo visible si MOSTRAR_PARAMS_AVANZADOS = true
        if (details) details.style.display = MOSTRAR_PARAMS_AVANZADOS ? '' : 'none';

        // VC: campos dentro de <details> colapsado. Otros: campos visibles directamente
        if (details && MOSTRAR_PARAMS_AVANZADOS) {
            if (isVolatilidadControlada) {
                // Mostrar como details colapsable, con listener para traducir al abrir
                details.removeAttribute('open');
                details.querySelector('summary').style.display = '';
                details.ontoggle = () => {
                    if (details.open) this._translateSlidersToFields();
                };
            } else {
                // Abrir y ocultar summary para que los campos sean visibles directamente
                details.setAttribute('open', '');
                details.querySelector('summary').style.display = 'none';
                details.ontoggle = null;
            }
        }

        // Renderizar campos dinámicos de config técnica.
        // Los RTP por canal (wallet/ticket/VLT) van a un contenedor SIEMPRE visible; el resto
        // de params técnicos queda en el <details> avanzado (oculto por MOSTRAR_PARAMS_AVANZADOS).
        // El guardado (collectResolverConfigFromForm) lee por id → da igual el contenedor DOM.
        const container = document.getElementById('resolverConfigFields');
        const containerRTP = document.getElementById('resolverConfigVisibleRTP');
        container.innerHTML = '';
        // Reset a oculto en cada render: si se cambia a un resolver sin RTP por canal, el
        // contenedor no debe quedar visible vacío (hueco fantasma). Se re-muestra abajo solo
        // si volatilidad-controlada efectivamente montó campos.
        if (containerRTP) {
            containerRTP.innerHTML = '';
            containerRTP.style.display = 'none';
        }

        if (resolver.configSchema.length === 0) {
            container.innerHTML =
                '<p style="color:var(--text-secondary); font-size:13px;">' +
                t('Este algoritmo no tiene parametros configurables.') +
                '</p>';
        } else {
            // Solo para volatilidad-controlada mostramos los RTP por canal sueltos arriba.
            const exponerRTPVisible = isVolatilidadControlada && containerRTP;
            for (let i = 0; i < resolver.configSchema.length; i++) {
                const field = resolver.configSchema[i];
                const destino =
                    exponerRTPVisible && CLAVES_DE_RTP_POR_CANAL.includes(field.key) ? containerRTP : container;
                destino.appendChild(this._createResolverConfigField(field, resolver.defaultConfig[field.key]));
            }
            if (exponerRTPVisible && containerRTP.children.length > 0) containerRTP.style.display = '';
        }

        // Refrescar presets para este resolver
        this._renderPresetCards();
    },

    /**
     * Traduce sliders actuales a config técnica y rellena los campos del formulario.
     * Se llama al abrir el <details> de parámetros avanzados.
     */
    async _translateSlidersToFields() {
        try {
            const sliders = this._collectSliderValues();
            const result = await Api.callAdmin('/admin/resolver-profiles/translate', 'POST', { sliders });
            if (!result || !result.config) return;

            const cfg = result.config;
            const keys = Object.keys(cfg);
            for (let i = 0; i < keys.length; i++) {
                const input = document.getElementById('resolverCfg_' + keys[i]);
                if (input) {
                    const val = cfg[keys[i]];
                    input.value = typeof val === 'object' ? JSON.stringify(val) : val;
                }
            }
        } catch (err) {
            // Silencioso — los campos quedan con defaults
        }
    },

    /**
     * Crea un elemento de formulario para un campo del configSchema.
     */
    _createResolverConfigField(field, currentValue) {
        const group = document.createElement('div');
        group.className = 'form-row';
        group.style.marginTop = '12px';

        const inner = document.createElement('div');
        inner.className = 'form-group';
        inner.style.flex = '1';

        const label = document.createElement('label');
        label.className = 'form-label';
        label.textContent = field.label;

        let input;
        if (field.type === 'number') {
            input = document.createElement('input');
            input.type = 'number';
            input.className = 'form-input';
            input.id = 'resolverCfg_' + field.key;
            input.value = currentValue !== undefined ? currentValue : field.default;
            if (field.min !== undefined) input.min = field.min;
            if (field.max !== undefined) input.max = field.max;
            if (field.step !== undefined) input.step = field.step;
        } else if (field.type === 'boolean') {
            input = document.createElement('select');
            input.className = 'form-select';
            input.id = 'resolverCfg_' + field.key;
            input.innerHTML = `<option value="true" ${currentValue ? 'selected' : ''}>Si</option>
                               <option value="false" ${!currentValue ? 'selected' : ''}>No</option>`;
        } else if (field.type === 'select') {
            input = document.createElement('select');
            input.className = 'form-select';
            input.id = 'resolverCfg_' + field.key;
            for (let j = 0; j < field.options.length; j++) {
                const o = field.options[j];
                const opt = document.createElement('option');
                opt.value = o.value;
                opt.textContent = o.label;
                if (o.value === currentValue) opt.selected = true;
                input.appendChild(opt);
            }
        } else if (field.type === 'json') {
            input = document.createElement('textarea');
            input.className = 'form-input';
            input.id = 'resolverCfg_' + field.key;
            input.rows = 6;
            input.style.fontFamily = 'monospace';
            input.style.fontSize = '12px';
            input.value = JSON.stringify(currentValue !== undefined ? currentValue : field.default, null, 2);
        }

        inner.appendChild(label);
        if (input) inner.appendChild(input);
        group.appendChild(inner);
        return group;
    },

    /**
     * Carga un preset: aplica su config a los campos del resolver.
     */
    loadPreset(presetId) {
        const preset = this._resolverPresetsCache?.find(p => p.id === Number(presetId));
        if (!preset) return;

        // Recordar el nombre del preset activo para persistirlo al guardar el sorteo
        this._activePresetNombre = preset.nombre;

        // Seleccionar el resolver del preset
        document.getElementById('resolverName').value = preset.resolver_name;
        this.onResolverChange();

        // Aplicar valores del preset a los campos
        const config = preset.config || JSON.parse(preset.config_json || '{}');
        const keys = Object.keys(config);
        for (let i = 0; i < keys.length; i++) {
            const input = document.getElementById('resolverCfg_' + keys[i]);
            if (!input) continue;
            const val = config[keys[i]];
            if (typeof val === 'object' && val !== null) {
                input.value = JSON.stringify(val, null, 2);
            } else {
                input.value = val;
            }
        }

        // Aplicar sliders si el preset los tiene (volatilidad-controlada)
        if (preset.sliders) {
            const sliderKeys = Object.keys(preset.sliders);
            for (let i = 0; i < sliderKeys.length; i++) {
                const slider = document.getElementById('slider_' + sliderKeys[i]);
                if (slider) slider.value = preset.sliders[sliderKeys[i]];
            }
        }

        App.toast(t('Preset cargado: ') + preset.nombre);
    },

    /**
     * Duplica un preset existente con nombre "Copia de X".
     */
    async duplicatePreset(presetId) {
        const preset = this._resolverPresetsCache?.find(p => p.id === Number(presetId));
        if (!preset) return;

        const nombre = prompt(t('Nombre del preset:'), t('Copia de ') + preset.nombre);
        if (!nombre) return;

        try {
            const config = preset.config || JSON.parse(preset.config_json || '{}');
            const payload = {
                game_type_code: 'lotowheel',
                resolver_name: preset.resolver_name,
                nombre,
                descripcion: preset.descripcion || '',
                config,
            };
            if (preset.sliders) payload.sliders = preset.sliders;

            const result = await Api.callAdmin('/admin/resolver-presets', 'POST', payload);
            if (result && result.id) {
                App.toast(t('Preset duplicado: ') + nombre);
                await this.loadResolverOptions();
            }
        } catch (err) {
            App.toast(err.message, 'error');
        }
    },

    /**
     * Renombra un preset (prompt + PUT a API).
     */
    async renamePreset(presetId) {
        const preset = this._resolverPresetsCache?.find(p => p.id === Number(presetId));
        if (!preset) return;

        const nombre = prompt(t('Nombre del preset:'), preset.nombre);
        if (!nombre || nombre === preset.nombre) return;

        try {
            await Api.callAdmin('/admin/resolver-presets/' + presetId, 'PUT', { nombre });
            App.toast(t('Preset renombrado: ') + nombre);
            await this.loadResolverOptions();
        } catch (err) {
            App.toast(err.message, 'error');
        }
    },

    /**
     * Elimina un preset por ID (sin restricción de sistema).
     */
    async deletePreset(presetId) {
        const preset = this._resolverPresetsCache?.find(p => p.id === Number(presetId));
        if (!preset) return;
        if (!confirm(t('Eliminar preset "') + preset.nombre + t('"?'))) return;

        try {
            await Api.callAdmin('/admin/resolver-presets/' + presetId, 'DELETE');
            App.toast(t('Preset eliminado'));
            await this.loadResolverOptions();
        } catch (err) {
            App.toast(err.message, 'error');
        }
    },

    /**
     * Recopila la config del resolver desde los campos dinámicos del formulario.
     */
    collectResolverConfigFromForm() {
        const resolverName = document.getElementById('resolverName').value;
        const resolver = this._resolverCache?.find(r => r.name === resolverName);
        if (!resolver) return { name: resolverName, config: {} };

        const config = {};
        for (let i = 0; i < resolver.configSchema.length; i++) {
            const field = resolver.configSchema[i];
            const input = document.getElementById('resolverCfg_' + field.key);
            if (!input) continue;

            if (field.type === 'number') {
                config[field.key] = parseFloat(input.value);
            } else if (field.type === 'boolean') {
                config[field.key] = input.value === 'true';
            } else if (field.type === 'json') {
                try {
                    config[field.key] = JSON.parse(input.value);
                } catch (e) {
                    config[field.key] = field.default;
                }
            } else {
                config[field.key] = input.value;
            }
        }

        const result = { name: resolverName, config };

        // Si hay un preset activo, incluir su nombre para mostrarlo en el card
        if (this._activePresetNombre) {
            result.presetNombre = this._activePresetNombre;
        }

        // Si es volatilidad-controlada con sliders activos, guardar los valores de sliders
        if (resolverName === 'volatilidad-controlada') {
            const profileSection = document.getElementById('profileSection');
            if (profileSection && profileSection.style.display !== 'none') {
                result.sliders = this._collectSliderValues();
            }
        }

        return result;
    },

    /**
     * Guarda la config actual del resolver como un nuevo preset.
     */
    async saveCurrentAsPreset() {
        const resolverName = document.getElementById('resolverName').value;
        const isVolatilidad = resolverName === 'volatilidad-controlada';
        const profileSection = document.getElementById('profileSection');
        const usarSliders = isVolatilidad && profileSection && profileSection.style.display !== 'none';

        let configToSave;
        let descripcion = '';

        if (usarSliders) {
            // Modo sliders: traducir sliders a config via API
            const sliders = this._collectSliderValues();
            try {
                const translated = await Api.callAdmin('/admin/resolver-profiles/translate', 'POST', { sliders });
                if (!translated || !translated.config) {
                    App.toast(t('Error generando config desde sliders'), 'error');
                    return;
                }
                // Los sliders son la autoridad de lo TÉCNICO, pero no conocen los RTP por canal:
                // el operador los edita en su propio bloque visible. Guardar solo la traducción
                // perdía lo que acababa de escribir (el preset volvía con el default); mezclar
                // todo el formulario encima habría restaurado técnica vieja del DOM y anulado el
                // cálculo de los sliders. Composición explícita: técnica + los tres targets.
                //
                // La lectura EXIGE los tres y lanza en el primero que no sirva: es configuración de
                // dinero, así que una entrada ilegible detiene el guardado en vez de caer al default
                // del resolver a espaldas del operador. El throw sale por el catch de abajo, que
                // muestra el error y retorna — el POST del preset no llega a ejecutarse.
                configToSave = componerConfigDePresetConRtpPorCanal(
                    translated.config,
                    leerRtpPorCanalExigiendoTodos(document)
                );
                descripcion =
                    'Perfil: R=' +
                    sliders.rentabilidad +
                    ' V=' +
                    sliders.volatilidad +
                    ' Rec=' +
                    sliders.recuperacion +
                    ' Ri=' +
                    sliders.riesgo;
            } catch (err) {
                App.toast(err.message, 'error');
                return;
            }
        } else {
            // Modo config directa: el operador editó los campos técnicos a mano.
            //
            // Los tres RTP por canal están en el DOM en los DOS modos —plegar los sliders solo
            // cambia quién manda en lo técnico—, así que la exigencia va también acá: si no
            // estuviera, bastaría guardar en modo directo para colar un valor ilegible por el mismo
            // botón. `collectResolverConfigFromForm` se deja laxa a propósito: también alimenta el
            // borrador del sorteo, donde lanzar perdería trabajo en curso del operador. La frontera
            // que persiste es esta.
            if (isVolatilidad) {
                try {
                    leerRtpPorCanalExigiendoTodos(document);
                } catch (err) {
                    App.toast(err.message, 'error');
                    return;
                }
            }
            const resolverConfig = this.collectResolverConfigFromForm();
            configToSave = resolverConfig.config;
        }

        const nombre = prompt(t('Nombre del preset:'));
        if (!nombre) return;

        if (!descripcion) {
            descripcion = prompt(t('Descripcion (opcional):')) || '';
        }

        try {
            const payload = {
                game_type_code: 'lotowheel',
                resolver_name: resolverName,
                nombre,
                descripcion,
                config: configToSave,
            };
            if (usarSliders) {
                payload.sliders = this._collectSliderValues();
            }
            const result = await Api.callAdmin('/admin/resolver-presets', 'POST', payload);

            if (result && result.id) {
                App.toast(t('Preset guardado: ') + nombre);
                await this.loadResolverOptions();
            }
        } catch (err) {
            App.toast(t('Error al guardar preset: ') + err.message, 'error');
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PERFIL OPERADOR — 4 sliders que mapean a params del resolver
    // (volatilidad, recorte, agresividad, peso histórico, escenario).
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Renderiza los 4 sliders y el dropdown de presets de fabrica.
     */
    _renderProfileSliders() {
        const container = document.getElementById('profileSlidersContainer');
        if (!container || !this._sliderDefinitions) return;

        container.innerHTML = '';
        for (let i = 0; i < this._sliderDefinitions.length; i++) {
            const def = this._sliderDefinitions[i];
            const marks = def.marks || [];

            // Generar marcas del track
            let marksHtml = '';
            for (let m = 0; m < marks.length; m++) {
                const mark = marks[m];
                const leftPct = mark.pos + '%';
                marksHtml +=
                    '<div class="ps-mark" style="left:' +
                    leftPct +
                    '">' +
                    '<div class="ps-mark-tick"></div>' +
                    '<div class="ps-mark-text">' +
                    mark.text +
                    '</div>' +
                    (mark.sub ? '<div class="ps-mark-sub">' + mark.sub + '</div>' : '') +
                    '</div>';
            }

            const row = document.createElement('div');
            row.className = 'profile-slider';
            row.innerHTML =
                '<div class="ps-header">' +
                '<span class="ps-name">' +
                def.label +
                '</span>' +
                '</div>' +
                '<div class="ps-desc">' +
                def.descripcion +
                '</div>' +
                '<div class="ps-track-wrapper">' +
                '<input type="range" min="0" max="100" value="' +
                def.default +
                '" ' +
                'id="slider_' +
                def.key +
                '" ' +
                'data-action="lotowheel:onSliderInput" data-key="' +
                def.key +
                '">' +
                '<div class="ps-marks">' +
                marksHtml +
                '</div>' +
                '</div>';
            container.appendChild(row);
        }
    },

    /**
     * Callback cuando el usuario mueve un slider.
     */
    _onSliderInput(key, value) {
        // Cerrar details de parámetros avanzados
        const details = document.getElementById('resolverConfigDetails');
        if (details) details.removeAttribute('open');

        // Si había un preset activo y el usuario mueve un slider, marcar como Modificado
        if (this._activePresetNombre) {
            const presetInput = document.getElementById('presetSearchInput');
            const presetClear = document.getElementById('presetSearchClear');
            if (presetInput) presetInput.value = t('Modificado');
            if (presetClear) presetClear.style.display = '';
            // Limpiar nombre: la config ya no corresponde exactamente al preset
            this._activePresetNombre = null;
        }
    },

    /**
     * Recoge los valores actuales de los 4 sliders.
     */
    _collectSliderValues() {
        const sliders = {};
        const keys = ['rentabilidad', 'volatilidad', 'recuperacion', 'riesgo'];
        for (let i = 0; i < keys.length; i++) {
            const input = document.getElementById('slider_' + keys[i]);
            sliders[keys[i]] = input ? Number(input.value) : 50;
        }
        return sliders;
    },

    /**
     * Abre el modal simulador dedicado con el resolver actual.
     */
    simulateProfile() {
        const currentResolver = document.getElementById('resolverName').value;
        let sliders = null;
        let resolverConfig = null;

        if (currentResolver === 'volatilidad-controlada') {
            sliders = this._collectSliderValues();
        } else {
            const collected = this.collectResolverConfigFromForm();
            resolverConfig = collected.config;
        }

        LotowheelSimulator.open(currentResolver, resolverConfig, sliders);
    },
});
